import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Smartphone, Share } from "lucide-react";
import { LOGO_URL } from "@/lib/api";

const DISMISS_KEY = "blisson_install_dismissed_v1";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < 7 * 24 * 60 * 60 * 1000) {
      return;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setTimeout(() => setVisible(true), 4000);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS does not fire beforeinstallprompt — show our own iOS instructions
    if (isIOS() && !isStandalone()) {
      setIos(true);
      setTimeout(() => setVisible(true), 5000);
    }

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
    if (choice.outcome !== "accepted") {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "tween", duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }}
          className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-auto md:right-6 md:w-[380px] z-[55] bg-white border border-black/10 luxury-shadow"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
          data-testid="install-prompt"
        >
          <div className="p-5 flex gap-4 items-start">
            <div className="w-12 h-12 bg-[#F8F5F0] flex items-center justify-center flex-shrink-0">
              <img src={LOGO_URL} alt="Blisson" className="w-10 h-10 object-contain mix-blend-multiply" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline mb-1">Install</div>
              <div className="font-serif-display text-xl leading-tight">
                Blisson on your <em className="gold-text not-italic">home screen</em>
              </div>
              {ios ? (
                <p className="text-xs text-[#5C5C5C] mt-2 leading-relaxed">
                  Tap <Share size={12} className="inline -mt-0.5" strokeWidth={1.6} /> Share, then{" "}
                  <strong>Add to Home Screen</strong> to install.
                </p>
              ) : (
                <p className="text-xs text-[#5C5C5C] mt-2 leading-relaxed">
                  Faster checkout, offline browsing, app-like experience — no app store needed.
                </p>
              )}

              <div className="flex items-center gap-2 mt-4">
                {!ios && deferred && (
                  <button
                    onClick={install}
                    className="flex items-center gap-2 bg-black text-white px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#1a1a1a] transition-colors"
                    data-testid="install-prompt-install"
                  >
                    <Download size={12} /> Install
                  </button>
                )}
                <button
                  onClick={dismiss}
                  className="text-[10px] tracking-[0.28em] uppercase text-[#5C5C5C] hover:text-black"
                  data-testid="install-prompt-dismiss"
                >
                  Not now
                </button>
              </div>
            </div>
            <button onClick={dismiss} className="p-1 -mr-1 -mt-1 text-[#5C5C5C]" aria-label="Close" data-testid="install-prompt-close">
              <X size={16} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
